import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import DataOps from './DataOps';
import { CampReviewEligibility, CampReviewListResponse, CampReviewRecord, CampReviewSummary } from '../interface/Review';

const REVIEWS_PER_PAGE = 12;

const EMPTY_SUMMARY: CampReviewSummary = {
    averageRating: 0,
    reviewCount: 0,
    ratingBreakdown: {},
};

const toDate = (value: any): Date | null => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (value?.toDate && typeof value.toDate === 'function') return value.toDate();
    if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);
    if (typeof value?._seconds === 'number') return new Date(value._seconds * 1000);
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const hasSessionEnded = (session: any): boolean => {
    const now = Date.now();
    const endCandidates = [session?.endAt, session?.officialEndDate, session?.endDate];
    for (const candidate of endCandidates) {
        const date = toDate(candidate);
        if (date && date.getTime() < now) return true;
    }
    return false;
};

class ReviewDAO {
    static async getCampReviews(
        campId: string,
        pageSize: number = REVIEWS_PER_PAGE
    ): Promise<CampReviewListResponse> {
        const cloudResult = await DataOps.fetchCloudData<CampReviewListResponse>(
            'getCampReviews',
            { campId, pageSize },
            {
                retries: 1,
                timeoutMs: 2500,
                suppressErrors: true,
                extractor: (json) => ({
                    reviews: Array.isArray(json?.reviews) ? json.reviews : [],
                    summary: json?.summary || EMPTY_SUMMARY,
                    nextCursor: json?.nextCursor ?? null,
                }),
            }
        );

        if (cloudResult && !Array.isArray(cloudResult)) {
            return {
                reviews: cloudResult.reviews || [],
                summary: cloudResult.summary || EMPTY_SUMMARY,
                nextCursor: cloudResult.nextCursor ?? null,
            };
        }

        // Fallback for local/dev when CF is unavailable.
        const reviewsRef = collection(DataOps.db, 'camp', campId, 'reviews');
        const q = query(reviewsRef, orderBy('createdAt', 'desc'), limit(pageSize));
        const snap = await getDocs(q);
        const reviews: CampReviewRecord[] = snap.docs.map((docSnap) => ({
            reviewId: docSnap.id,
            ...(docSnap.data() as Omit<CampReviewRecord, 'reviewId'>),
        }));

        const reviewCount = reviews.length;
        const averageRating = reviewCount > 0
            ? reviews.reduce((sum, review) => sum + Number(review.rating || 0), 0) / reviewCount
            : 0;

        return {
            reviews,
            summary: {
                averageRating: Number(averageRating.toFixed(2)),
                reviewCount,
                ratingBreakdown: {},
            },
            nextCursor: null,
        };
    }

    static async getReviewEligibility(campId: string, userId?: string | null): Promise<CampReviewEligibility> {
        if (!userId) {
            return {
                eligible: false,
                reasons: ['Please log in to leave a review.'],
                eligibleSessions: [],
            };
        }

        const cloudResult = await DataOps.fetchCloudData<CampReviewEligibility>(
            'getCampReviewEligibility',
            { campId, userId },
            {
                retries: 1,
                timeoutMs: 2500,
                suppressErrors: true,
                extractor: (json) => ({
                    eligible: Boolean(json?.eligible),
                    reasons: Array.isArray(json?.reasons) ? json.reasons : [],
                    eligibleSessions: Array.isArray(json?.eligibleSessions) ? json.eligibleSessions : [],
                }),
            }
        );

        if (cloudResult && !Array.isArray(cloudResult)) {
            return cloudResult;
        }

        // Fallback logic for local/dev:
        // user must have a confirmed registration in this camp, and the session must have ended,
        // and they must not have already reviewed that session.
        const registrations = await DataOps.fetchActiveRegistrationsByUser(userId);
        const campRegistrations = (registrations || []).filter((registration: any) => registration.campId === campId);

        if (campRegistrations.length === 0) {
            return {
                eligible: false,
                reasons: ['Only families with completed sessions can leave a review.'],
                eligibleSessions: [],
            };
        }

        const reviewedSessionIds = new Set<string>();
        try {
            const reviewsRef = collection(DataOps.db, 'camp', campId, 'reviews');
            const reviewedQuery = query(reviewsRef, where('userId', '==', userId));
            const reviewedSnap = await getDocs(reviewedQuery);
            reviewedSnap.docs.forEach((docSnap) => {
                const data: any = docSnap.data();
                if (data?.sessionId) reviewedSessionIds.add(String(data.sessionId));
            });
        } catch {}

        const eligibleSessions: Array<{ sessionId: string; sessionTitle: string; endedAt?: any }> = [];

        for (const registration of campRegistrations) {
            const sid = registration?.sessionId;
            if (!sid || reviewedSessionIds.has(String(sid))) continue;
            const session = await DataOps.fetchDataFromFirebase('session', sid, true);
            if (!session || !hasSessionEnded(session)) continue;
            eligibleSessions.push({
                sessionId: String(sid),
                sessionTitle: session?.title || session?.sessionTitle || 'Camp Session',
                endedAt: session?.endAt || session?.officialEndDate || session?.endDate || null,
            });
        }

        return {
            eligible: eligibleSessions.length > 0,
            reasons: eligibleSessions.length > 0 ? [] : ['A review can be submitted after a registered session is completed.'],
            eligibleSessions,
        };
    }

    static async submitCampReview(payload: {
        campId: string;
        userId: string;
        sessionId: string;
        rating: number;
        comment: string;
    }): Promise<{ success: boolean; review?: CampReviewRecord; error?: string }> {
        const result = await DataOps.postCloudData<{ success: boolean; review?: CampReviewRecord; error?: string }>(
            'submitCampReview',
            payload,
            {
                retries: 1,
                timeoutMs: 5000,
                suppressErrors: true,
                extractor: (json) => ({
                    success: Boolean(json?.success),
                    review: json?.review,
                    error: json?.error,
                }),
            }
        );

        if (result && !Array.isArray(result)) return result;
        return { success: false, error: 'Unable to submit review right now.' };
    }
}

export default ReviewDAO;
